import React, { useCallback, useEffect, useRef, useState } from "react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import ListPersonAvatar from "../../../Components/Table/ListPersonAvatar";
import { ToolbarFilterSelect } from "../../../Components/Table/toolbarFilters";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_recruitment_application_id",
  sortOrder: "desc",
};

const DEFAULT_COLUMNS = [
  { field: "applicant_name", label: "Applicant Name", visible: true, width: 240, filterable: true, sortable: true, pinned: "none", required: false, order: 0 },
  { field: "applied_for", label: "Applied For", visible: true, width: 200, filterable: true, sortable: true, pinned: "none", required: false, order: 1 },
  { field: "total_experience_years", label: "Exp (Years)", visible: true, width: 110, filterable: true, sortable: true, pinned: "none", required: false, order: 2 },
  { field: "education", label: "Education", visible: true, width: 140, filterable: true, sortable: true, pinned: "none", required: false, order: 3 },
  { field: "assessment", label: "Assessment", visible: true, width: 120, filterable: true, sortable: false, pinned: "none", required: false, order: 4 },
  { field: "expected_salary", label: "Expected", visible: true, width: 120, filterable: true, sortable: true, pinned: "none", required: false, order: 5 },
  { field: "application_date_display", label: "Application Date", visible: true, width: 160, filterable: true, sortable: true, pinned: "none", required: false, order: 6 },
];

const dash = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const formatDisplayDate = (value) => {
  if (!value) return "—";
  const s = String(value).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
};

const formatAssessment = (item) => {
  const parts = [];
  if (hasValue(item?.communication_marks)) {
    parts.push(`C${Number(item.communication_marks)}`);
  }
  if (hasValue(item?.interview_marks)) {
    parts.push(`I${Number(item.interview_marks)}`);
  }
  return parts.length ? parts.join("/") : "—";
};

const TwoLineCell = ({ primary, secondary }) => (
  <div className="ems-list-twoline">
    <div className="ems-list-twoline__primary">
      {hasValue(primary) ? primary : "—"}
    </div>
    {hasValue(secondary) ? (
      <div className="ems-list-twoline__secondary">{secondary}</div>
    ) : null}
  </div>
);

const ApplicantNameCell = ({ row, name, city }) => (
  <div className="ems-list-person">
    <ListPersonAvatar
      row={row}
      getNestedValue={(r, key) => r?.[key]}
      size={34}
    />
    <TwoLineCell primary={name} secondary={city} />
  </div>
);

const SingleLineCell = ({ value }) => (
  <div className="ems-list-oneline">{dash(value)}</div>
);

const asList = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    const val = payload?.[key];
    if (Array.isArray(val)) return val;
    if (Array.isArray(val?.data)) return val.data;
  }
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const mergeToolbarFilters = (filters, searchParams) => {
  const next = (Array.isArray(filters) ? filters : []).filter(
    (f) =>
      f?.field !== "organization_recruitment_opening_id" &&
      f?.field !== "organization_functional_role_id"
  );
  const openingId = searchParams.get("opening_id");
  const appliedFor = searchParams.get("applied_for");
  if (openingId) {
    next.push({
      field: "organization_recruitment_opening_id",
      operator: "equals",
      value: [openingId],
    });
  }
  if (appliedFor) {
    next.push({
      field: "organization_functional_role_id",
      operator: "equals",
      value: [appliedFor],
    });
  }
  return next;
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    ) {
      return;
    }

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;
      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => {
            const trimmed = v.trim();
            return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
          });
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }
      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "first_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

const normalizeAppliedFilters = (filters) => {
  if (!Array.isArray(filters)) return [];

  return filters
    .filter((f) => f?.field && f?.operator)
    .map((f) => {
      const requiresValue = operatorRequiresValue(f.operator);
      if (!requiresValue) {
        return { field: f.field, operator: f.operator, value: null };
      }

      let value = f.value;
      if (value === null || value === undefined || value === "") return null;
      if (Array.isArray(value) && value.length === 0) return null;

      if (["equals", "not_equals"].includes(f.operator) && typeof value === "string") {
        value = value.split(",").map((v) => {
          const trimmed = v.trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      } else if (
        ["equals", "not_equals"].includes(f.operator) &&
        Array.isArray(value)
      ) {
        value = value.map((v) => {
          const trimmed = String(v).trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      }

      return { field: f.field, operator: f.operator, value };
    })
    .filter(Boolean);
};

function RecruitmentApplicationsList() {
  const [rows, setRows] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const configColumns = DEFAULT_COLUMNS;
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [openings, setOpenings] = useState([]);
  const [functionalRoles, setFunctionalRoles] = useState([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  useEffect(() => {
    if (!org?.organization_id) return;
    const headers = getApiHeaders();
    const loadOptions = async () => {
      try {
        const [openingRes, roleRes] = await Promise.allSettled([
          fetch(
            `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-opening`,
            { headers }
          ),
          fetch(
            `${MAIN_URL}/api/organizations/${org.organization_id}/func-role?per_page=all`,
            { headers }
          ),
        ]);
        if (openingRes.status === "fulfilled" && openingRes.value.ok) {
          const payload = await openingRes.value.json();
          setOpenings(asList(payload, ["recruitment_openings", "data"]));
        }
        if (roleRes.status === "fulfilled" && roleRes.value.ok) {
          const payload = await roleRes.value.json();
          setFunctionalRoles(asList(payload, ["functional", "data"]));
        }
      } catch (error) {
        console.error(error);
      }
    };
    loadOptions();
  }, [org?.organization_id]);

  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) setSearchParams(params, { replace: true });
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;
    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const requestBody = {
          filters: mergeToolbarFilters(
            appliedFilters !== null
              ? normalizeAppliedFilters(appliedFilters)
              : buildFiltersFromParams(searchParams),
            searchParams
          ),
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field:
              searchParams.get("sortBy") ||
              "organization_recruitment_application_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-applications-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page: result.pagination.page || 1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setRows(
          data.map((item) => {
            const fullName =
              item?.full_name ||
              [item?.first_name, item?.middle_name, item?.last_name]
                .filter(Boolean)
                .join(" ") ||
              "";
            // Secondary under name: city (mock: Amritsar); fall back to entity / current org.
            const cityLabel =
              item?.city_name ||
              item?.entity_name ||
              item?.current_organization_name ||
              "";
            // Primary role: functional role, else opening title.
            const roleName =
              item?.functional_role_name || item?.opening_title || "";
            // Secondary under role: specialization (mock: Graphic Designing), else opening type.
            const specialization =
              item?.functional_role_specialization_name ||
              item?.opening_type ||
              "";
            const status = item?.application_status || "";
            const dateLabel = formatDisplayDate(item?.application_date);
            const expectedValue = hasValue(item?.expected_salary)
              ? item.expected_salary
              : item?.expected_stipend;

            return {
              ...item,
              id: item?.organization_recruitment_application_id,
              full_name: dash(fullName),
              applicant_name: (
                <ApplicantNameCell
                  row={item}
                  name={fullName}
                  city={cityLabel}
                />
              ),
              applied_for: (
                <TwoLineCell primary={roleName} secondary={specialization} />
              ),
              total_experience_years: (
                <SingleLineCell value={item?.total_experience_years} />
              ),
              education: <SingleLineCell value={item?.education} />,
              assessment: <SingleLineCell value={formatAssessment(item)} />,
              expected_salary: <SingleLineCell value={expectedValue} />,
              application_date_display: (
                <TwoLineCell primary={dateLabel} secondary={status} />
              ),
              application_date: dateLabel,
              application_status: dash(status),
            };
          })
        );
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error(error);
        toast.error("Failed to load recruitment applications");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => fetchControllerRef.current?.abort();
  }, [org?.organization_id, searchParams, appliedFilters]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-application/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Application deleted");
          setAppliedFilters((prev) => (prev === null ? [] : [...prev]));
        } else {
          const errorData = await response.json();
          toast.error(errorData.message || "Failed to delete application");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete application");
      }
    },
    [org?.organization_id]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/recruitment/applications/edit/${item.id || item.organization_recruitment_application_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/recruitment/applications/view/${item.id || item.organization_recruitment_application_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback((nextFilters) => {
    setAppliedFilters(Array.isArray(nextFilters) ? nextFilters : []);
  }, []);

  const handleToolbarFilterChange = useCallback(
    (key, value) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  return (
    <Layout4
      loading={false}
      heading={"Recruitment Applications"}
      btnName={"Add Application"}
      add_action={"RECRUITMENT_SOURCES_ADD"}
      Route={"/recruitment/applications"}
    >
      <TableDataGeneric
        tableName="organization_recruitment_applications"
        primaryKey="organization_recruitment_application_id"
        heading="Recruitment Applications"
        data={rows}
        sortname="organization_recruitment_application_id"
        showActions={true}
        Route="/recruitment/applications"
        DeleteFunc={handleDelete}
        EditFunc={handleEdit}
        handleShow={handleShow}
        onSortChange={handleSortChange}
        onFilterChange={handleFilterChange}
        edit_delete_action={[
          "RECRUITMENT_SOURCES_DELETE",
          "RECRUITMENT_SOURCES_EDIT",
        ]}
        token={localStorage.getItem("token")}
        configss={configColumns}
        preferConfigss={true}
        skipBackendConfigLoad={true}
        paginationData={paginationData}
        filterModule="organization_recruitment_applications"
        loading={loading}
        toolbarExtras={
          <>
            <ToolbarFilterSelect
              label="Opening"
              urlKey="opening_id"
              allLabel="All openings"
              options={openings.map((opening) => ({
                ...opening,
                opening_title:
                  opening.opening_title ||
                  `Opening ${opening.organization_recruitment_opening_id}`,
              }))}
              valueKey="organization_recruitment_opening_id"
              labelKey="opening_title"
              searchParams={searchParams}
              onChange={handleToolbarFilterChange}
              wide
            />
            <ToolbarFilterSelect
              label="Applied For"
              urlKey="applied_for"
              allLabel="All applied for"
              options={functionalRoles.map((role) => ({
                ...role,
                functional_role_name:
                  role.functional_role_name ||
                  `Role ${role.organization_functional_role_id}`,
              }))}
              valueKey="organization_functional_role_id"
              labelKey="functional_role_name"
              searchParams={searchParams}
              onChange={handleToolbarFilterChange}
              wide
            />
          </>
        }
      />
    </Layout4>
  );
}

export default RecruitmentApplicationsList;
